// services/socketService.ts
import { io, Socket } from 'socket.io-client';
import { API_CONFIG } from '../config/api';

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private connectPromise: Promise<void> | null = null;
  private isConnecting = false;

  // Идемпотентное подключение
  connect(accessToken: string, initialDelayMs = 500 + Math.floor(Math.random() * 2500)): Promise<void> {
    if (this.isConnected && this.socket?.connected) return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;

    this.isConnecting = true;

    this.connectPromise = (async () => {
      // небольшой джиттер, чтобы избежать «стада» при рестарте сервера
      await new Promise((r) => setTimeout(r, initialDelayMs));

      if (this.socket) return; // уже создали где-то ещё

      this.socket = io(API_CONFIG.SOCKET_URL, {
        auth: { token: accessToken },
        transports: ['websocket'],            // без polling
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 3000,
        reconnectionDelayMax: 60000,
        randomizationFactor: 0.7,            // джиттер
        timeout: 20000,
        forceNew: false,                      // критично: НЕ создавать новый менеджер
      });

      this.socket.on('connect', () => {
        this.isConnected = true;
        this.isConnecting = false;
      });

      // Не роняем промис/стейт: даём socket.io самому ретраить
      this.socket.on('connect_error', (_error) => {
        this.isConnected = false;
      });

      this.socket.on('disconnect', (reason) => {
        this.isConnected = false;
        this.isConnecting = true; // остаёмся в "пытаемся"
      });

      // ваши бизнес-события оставляем как были…

      // ВАЖНО: больше НЕТ ручного setTimeout/reject — пусть работает встроенный backoff
    })().finally(() => {
      this.connectPromise = null; // чтобы следующие вызовы могли проверить актуальное состояние
    });

    return this.connectPromise;
  }

  /**
   * Подставляет новый accessToken и переподключает transport (после silent refresh).
   */
  reconnectWithToken(accessToken: string): void {
    if (!this.socket) return;
    (this.socket as Socket & { auth?: { token?: string } }).auth = {
      token: accessToken,
    };
    if (this.socket.connected) {
      this.socket.disconnect();
    }
    this.isConnected = false;
    this.socket.connect();
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
    this.connectPromise = null;
  }

  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  // Получение экземпляра сокета
  getSocket(): Socket | null {
    return this.socket;
  }

  // Отправка событий наружу
  emit(event: string, ...args: any[]): void {
    this.socket?.emit(event, ...args);
  }

  // Прокси для подписок
  on(event: string, callback: (...args: any[]) => void): void {
    this.socket?.on(event, callback);
  }

  off(event: string, callback?: (...args: any[]) => void): void {
    this.socket?.off(event, callback);
  }

  once(event: string, callback: (...args: any[]) => void): void {
    this.socket?.once(event, callback);
  }
}

export const socketService = new SocketService();
